/**
 * Arbeidsgiverkostnader from PowerOffice Go (#183 phase 3): holiday pay %, the employer contribution
 * rate of the company's zone and the full week, read from Go (GET /admin/poweroffice/labour) and
 * shown beside what Timefly has. Each is taken on its own «Bruk», through the same saves the fields
 * above use; nothing changes before that, and nothing is written to PowerOffice.
 */
import { errorText, get } from '@/api/client';
import { useTenantWrite } from '@/api/data';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { POWEROFFICE, type PayrollSystem } from './payrollSystem';
import { useConnected } from './useConnected';

interface FromGo {
  holiday_pay_pct: number | null;
  full_week_hours: number | null;
  employer_zone: string | null;
  zones: string[];
  employer_rule: string | null;
  employer_tax_pct: number | null;
  /** Tripletex only (#187): virksomheter whose municipality has no zone there */
  unzoned?: string[];
  /** Tripletex only (#187): its pension schemes; it keeps no percentage on them */
  pension_schemes?: { number: string | null; from: string | null }[];
}
const n = (v: number) => String(v).replace('.', ',');
const zoneName = (z: string) =>
  z
    .replace('Zone', '')
    .toUpperCase()
    .replace(/^(\d)A$/, '$1a');

export function PowerOfficeRates({
  aga,
  holiday,
  onUse,
  sys = POWEROFFICE,
}: {
  aga: number | undefined;
  holiday: number | undefined;
  onUse: (p: { agaPct?: number; holidayPct?: number }) => void;
  /** the company's payroll system (#187) */
  sys?: PayrollSystem;
}) {
  const { tenant } = useTenant();
  const tw = useTenantWrite();
  const connected = useConnected(sys, tenant?.payroll_system === sys.key);
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: [sys.key, 'labour'],
    queryFn: () => get<FromGo>(`${sys.base}/labour`),
    enabled: open,
  });
  if (!connected) return null;
  if (!open)
    return (
      <p className="hint" style={{ padding: '0 12px' }}>
        <Button small onClick={() => setOpen(true)}>
          {T('Hent fra {system}', { system: sys.short })}
        </Button>
      </p>
    );
  const g = q.data;
  const week = Number(tenant?.agreed_week_hours ?? 37.5);
  const rows = g
    ? [
        g.employer_tax_pct != null && g.employer_tax_pct !== aga
          ? {
              k: 'aga',
              label: T('Arbeidsgiveravgift, sone {zone}: {pct} %', {
                zone: zoneName(g.employer_zone ?? ''),
                pct: n(g.employer_tax_pct),
              }),
              use: () => onUse({ agaPct: g.employer_tax_pct as number }),
            }
          : null,
        g.holiday_pay_pct != null && g.holiday_pay_pct !== holiday
          ? {
              k: 'hol',
              label: T('Feriepenger: {pct} %', { pct: n(g.holiday_pay_pct) }),
              use: () => onUse({ holidayPct: g.holiday_pay_pct as number }),
            }
          : null,
        g.full_week_hours != null && g.full_week_hours !== week && tenant
          ? {
              k: 'week',
              label: T('Full uke: {h} timer', { h: n(g.full_week_hours) }),
              use: () =>
                tw.mutate(
                  { id: tenant.id, body: { agreed_week_hours: String(g.full_week_hours) } },
                  { onSuccess: () => toast(T('Lagret')), onError: (e) => toast(errorText(e)) },
                ),
            }
          : null,
      ].filter((r) => r !== null)
    : [];
  return (
    <div className="hint" style={{ padding: '0 12px', display: 'grid', gap: 6 }} data-testid="po-rates">
      {q.isPending ? (
        <span>{T('Henter fra {system} …', { system: sys.short })}</span>
      ) : q.isError || !g ? (
        <span>{errorText(q.error)}</span>
      ) : (
        <>
          <b>{T('I {system}', { system: sys.short })}</b>
          {rows.length === 0 && (
            <span>{T('Satsene i Timefly er de samme som i {system}.', { system: sys.short })}</span>
          )}
          {rows.map((r) => (
            <span key={r.k} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {r.label}
              <Button small onClick={r.use}>
                {T('Bruk')}
              </Button>
            </span>
          ))}
          {g.employer_tax_pct == null && g.zones.length > 0 && (
            <span>
              {g.zones.length > 1
                ? T('Bedriften har virksomheter i flere soner ({zones}). Velg satsen selv.', {
                    zones: g.zones.map(zoneName).join(', '),
                  })
                : T('{system} har en egen ordning for arbeidsgiveravgift her. Velg satsen selv.', {
                    system: sys.short,
                  })}
            </span>
          )}
          {(g.unzoned?.length ?? 0) > 0 && (
            <span>
              {T('{names} ligger i en kommune uten sone i {system}. Velg satsen selv.', {
                names: (g.unzoned ?? []).join(', '),
                system: sys.short,
              })}
            </span>
          )}
          {g.pension_schemes && (
            <span data-testid="pension-schemes">
              {g.pension_schemes.length > 0
                ? T(
                    'Pensjonsordning i {system}: {list}. {system} oppgir ikke satsen, så pensjonen settes over.',
                    {
                      system: sys.short,
                      list: g.pension_schemes
                        .map(
                          (p) =>
                            `${T('nr')} ${p.number ?? '?'}${p.from ? ` ${T('fra')} ${fmtD(p.from)}` : ''}`,
                        )
                        .join(', '),
                    },
                  )
                : T('Ingen pensjonsordning er registrert i {system}.', { system: sys.short })}
            </span>
          )}
          {g.employer_zone === 'Zone1a' && (
            <span>{T('Sone 1a: 10,6 % til fribeløpet på 850 000 kr er brukt, deretter 14,1 %.')}</span>
          )}
        </>
      )}
    </div>
  );
}
